import { injectable } from 'inversify';
import {
  ArmyListUnit,
  ArmyListUnitModelRuleTroop,
  ChangeProfilAction,
  ChangeProfilCarac,
  Nullable,
  TroopCarac,
  Unit,
  UnitCarac,
  UnitOptionChangeModelRuleAction,
} from '@Models';
import '@Extensions';

@injectable()
export class ArmyListUnitUnitOptionChangeProfilAction {
  public updateUnitProfil(
    armyListUnit: ArmyListUnit,
    unit: Unit,
  ): ArmyListUnit {
    let result = {
      ...armyListUnit,
      carac: {
        ...armyListUnit.carac,
        unitBase:
          armyListUnit.mount?.carac?.unitBase ?? armyListUnit.carac.unitBase,
        unitHeight:
          armyListUnit.mount?.carac?.unitHeight ??
          armyListUnit.carac.unitHeight,
      },
      troops: armyListUnit.troops.map((troop) => ({ ...troop })),
      modelRuleUnitTroops: armyListUnit.modelRuleUnitTroops.map((rule) => ({
        ...rule,
      })),
    } satisfies ArmyListUnit as ArmyListUnit;

    result = this.modifyUnitCarac(result, unit);
    result = this.modifyUnitModelRule(result, unit);

    return result;
  }

  public modifyUnitCarac(armyListUnit: ArmyListUnit, unit: Unit): ArmyListUnit {
    if (armyListUnit.unit_id === unit.id) {
      armyListUnit.carac = {
        ...unit.carac,
      };

      armyListUnit.troops.forEach((troop) => {
        troop.carac = {
          ...unit.troops.find((item) => item.id === troop.troopId)!.carac,
        };
      });
    }

    const selectedUnitOptionIds = armyListUnit.army_list_unit_unit_options.map(
      (item) => item.unit_option_id,
    );

    const changeOptions = unit.optionGroups
      .flatMap((o) => o.options)
      .filter((unitOption) => unitOption.changeProfil)
      .filter((unitOption) => selectedUnitOptionIds.includes(unitOption.id));

    changeOptions.forEach((unitOption) => {
      armyListUnit.carac.unitHeight =
        unitOption.unitHeight || armyListUnit.carac.unitHeight;
      armyListUnit.carac.unitBase =
        unitOption.unitBase || armyListUnit.carac.unitBase;
    });

    const changeProfils = changeOptions
      .flatMap((unitOption) => unitOption.changeProfils)
      .map((change) => {
        return {
          carac: change.caracs,
          troop_id: change.troopId,
          action: change.action,
          value:
            change.action === ChangeProfilAction.Replace
              ? change.value
              : `+${change.value}`,
        };
      })
      .sort((a, b) => {
        if (a.action != b.action) return a.action - b.action;

        return a.value.localeCompare(b.value);
      });

    changeProfils
      .filter((change) => change.troop_id === null)
      .groupBy((change) => change.carac)
      .forEach((value, key) => {
        armyListUnit.carac = this.replaceUnitCarac(
          armyListUnit.carac,
          key as ChangeProfilCarac,
          `${
            this.getUnitCarac(armyListUnit.carac, key as ChangeProfilCarac) ??
            ''
          } (${value.map((item) => item.value).join(', ')})`,
        );
      });

    changeProfils
      .filter((change) => change.troop_id !== null)
      .groupBy((change) => change.troop_id!)
      .forEach((value, key) => {
        const troop = armyListUnit.troops.find((troop) => troop.id === key);
        if (!troop) return;

        value
          .groupBy((change) => change.carac)
          .forEach((value, key) => {
            troop.carac = this.replaceTroopCarac(
              troop.carac,
              key as ChangeProfilCarac,
              `${
                this.getTroopCarac(troop.carac, key as ChangeProfilCarac) ?? ''
              } (${value.map((item) => item.value).join(', ')})`,
            );
          });
      });

    return armyListUnit;
  }

  public modifyUnitModelRule(
    armyListUnit: ArmyListUnit,
    unit: Unit,
  ): ArmyListUnit {
    const selectedUnitOptionIds = armyListUnit.army_list_unit_unit_options.map(
      (item) => item.unit_option_id,
    );

    unit.modelRuleUnitTroops
      .filter(
        (modelRule) =>
          !armyListUnit.modelRuleUnitTroops.find(
            (item) => item.modelRuleId === modelRule.modelRuleId,
          ),
      )
      .forEach((modelRule) => {
        armyListUnit.modelRuleUnitTroops.push({
          id: '',
          modelRuleId: modelRule.modelRuleId,
          unitId: modelRule.unitId,
          unitOptionId: null,
          troopId: modelRule.troopId,
          name: modelRule.name,
          description: modelRule.description,
          info: modelRule.info,
          typeLvl: modelRule.typeLvl,
          modelRuleUnitTroopId: modelRule.id,
          isEnabled: true,
          unitOptionChangeModelRuleId: null,
          isVisible: true,
          typeRule: modelRule.typeRule,
          isSelected: false,
        } satisfies ArmyListUnitModelRuleTroop as ArmyListUnitModelRuleTroop);
      });

    const changeModelRules = unit.optionGroups
      .flatMap((o) => o.options)
      .filter((unitOption) => unitOption.changeProfil)
      .filter((unitOption) => selectedUnitOptionIds.includes(unitOption.id))
      .flatMap((unitOption) => unitOption.changeModelRules);

    const enableModelRules = changeModelRules.filter(
      (change) => change.action == UnitOptionChangeModelRuleAction.Enable,
    );
    const disableModelRules = changeModelRules.filter(
      (change) => change.action == UnitOptionChangeModelRuleAction.Disable,
    );

    const modelRules = armyListUnit.modelRuleUnitTroops;

    disableModelRules.forEach((change) => {
      const armyListUnitModelRule = modelRules.find(
        (item) => change.modelRuleId === item.modelRuleId,
      );
      if (armyListUnitModelRule) {
        armyListUnitModelRule.isEnabled = false;
      }
    });

    enableModelRules.forEach((change) => {
      const armyListUnitModelRule = modelRules.find(
        (item) => change.modelRuleId === item.modelRuleId,
      );
      if (armyListUnitModelRule) {
        armyListUnitModelRule.isEnabled = true;
      } else {
        modelRules.push({
          id: '',
          modelRuleId: change.modelRuleId,
          unitId: change.unitId,
          unitOptionId: change.unitOptionId,
          troopId: change.troopId,
          name: change.name,
          description: change.description,
          info: change.info,
          typeLvl: change.typeLvl,
          modelRuleUnitTroopId: null,
          isEnabled: true,
          unitOptionChangeModelRuleId: change.id,
          isVisible: true,
          typeRule: change.typeRule,
          isSelected: false,
        });
      }
    });

    armyListUnit.modelRuleUnitTroops = modelRules;

    return armyListUnit;
  }

  // private methods
  public replaceUnitCarac(
    unitCarac: UnitCarac,
    profil: ChangeProfilCarac,
    value: Nullable<string>,
  ): UnitCarac {
    return {
      ...unitCarac,
      [ChangeProfilCarac[profil] as keyof TroopCarac]: value,
    } satisfies UnitCarac as UnitCarac;
  }

  public getUnitCarac(
    unitCarac: UnitCarac,
    profil: ChangeProfilCarac,
  ): Nullable<string> {
    return (
      unitCarac[ChangeProfilCarac[profil] as keyof UnitCarac]?.toString() ??
      null
    );
  }

  public replaceTroopCarac(
    troopCarac: TroopCarac,
    profil: ChangeProfilCarac,
    value: Nullable<string>,
  ): TroopCarac {
    return {
      ...troopCarac,
      [ChangeProfilCarac[profil] as keyof TroopCarac]: value,
    } satisfies TroopCarac as TroopCarac;
  }

  public getTroopCarac(
    troopCarac: TroopCarac,
    profil: ChangeProfilCarac,
  ): string | null {
    return (
      troopCarac[ChangeProfilCarac[profil] as keyof TroopCarac]?.toString() ??
      null
    );
  }
}
